/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
  Button,
  IconButton,
  Pill,
  RiAddLine,
  RiArrowDownSLine,
  RiDeleteBinFill,
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  Stack,
  TextInput,
} from 'component-library';
import { useFieldArray } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { RoadmapNode, RoadmapNodeStatus } from '@/api/rest/resources/types/organization';
import { FormControl, FormField, FormItem } from '@/components';
import { isNullish } from '@/utils/booleans';

import {
  EMPTY_MILESTONE,
  ROOT_ROADMAP_NODE_INDEX,
  useWorkstreamCreateContext,
  WORKSTREAM_FORM_KEYS,
} from '../hooks/useFormControls';

export const WorkstreamsMilestonesForm = ({ form }: { form: ReturnType<typeof useWorkstreamCreateContext> }) => {
  const { t } = useTranslation();
  const { fields, append, remove } = useFieldArray({
    control: form.control,
    name: `${WORKSTREAM_FORM_KEYS.MILESTONES}.${ROOT_ROADMAP_NODE_INDEX}.children` as any,
  });

  return (
    <Stack spacing={4} className='rounded-lg border border-divider p-6'>
      <Stack direction='row' centerMain className='justify-between'>
        <span className='typography-h3 text-text-primary'>{t('global.workstream.labels.milestones')}</span>
        <Button
          variant='outline'
          className='w-fit'
          onClick={() => append(EMPTY_MILESTONE)}
          leftAdornment={<RiAddLine />}
        >
          {t('global.workstream.actions.addMilestone')}
        </Button>
      </Stack>
      {fields.length === 0 && (
        <span className='typography-body1 text-text-secondary'>{t('global.workstream.labels.noMilestonesHint')}</span>
      )}
      <Accordion type='multiple' className='flex flex-col gap-3'>
        {fields.map((field, index) => (
          <MilestoneItem key={field.id} form={form} index={index} itemValue={field.id} onRemove={() => remove(index)} />
        ))}
      </Accordion>
    </Stack>
  );
};

const MilestoneItem = ({
  form,
  index,
  itemValue,
  onRemove,
}: {
  form: ReturnType<typeof useWorkstreamCreateContext>;
  index: number;
  itemValue: string;
  onRemove: () => void;
}) => {
  return (
    <AccordionItem value={itemValue} className='rounded-none bg-transparent'>
      <Stack direction='row' spacing={4} centerMain className='px-3 py-2'>
        <AccordionTrigger className='group/trigger flex-none shrink-0 p-0 hover:bg-transparent [&>svg:last-child]:hidden'>
          <RiArrowDownSLine className='-rotate-90 transition-transform group-data-[state=open]/trigger:rotate-0 text-text-secondary' />
        </AccordionTrigger>
        <MileStoneForm form={form} index={index} onRemove={onRemove} />
      </Stack>
      <AccordionContent className='p-0'>
        <StepsList form={form} milestoneIndex={index} />
      </AccordionContent>
    </AccordionItem>
  );
};

const MileStoneForm = ({
  form,
  index,
  onRemove,
  milestoneIndex,
}: {
  form: ReturnType<typeof useWorkstreamCreateContext>;
  index: number;
  onRemove: () => void;
  milestoneIndex?: number;
}) => {
  const { t } = useTranslation();

  const indexDisplay = milestoneIndex !== undefined ? `${milestoneIndex + 1}.${index + 1}` : `${index + 1}`;
  const fieldBaseName =
    milestoneIndex !== undefined
      ? (`${WORKSTREAM_FORM_KEYS.MILESTONES}.${ROOT_ROADMAP_NODE_INDEX}.children.${milestoneIndex}.children.${index}` as const)
      : (`${WORKSTREAM_FORM_KEYS.MILESTONES}.${ROOT_ROADMAP_NODE_INDEX}.children.${index}` as const);
  const childrenCount = isNullish(milestoneIndex)
    ? ((
        form.getValues(
          `${WORKSTREAM_FORM_KEYS.MILESTONES}.${ROOT_ROADMAP_NODE_INDEX}.children.${index}.children` as any,
        ) as Partial<RoadmapNode>[] | undefined
      )?.length ?? 0)
    : null;

  return (
    <Stack className='px-3 w-full'>
      <Stack direction='row' className='h-full' spacing={4}>
        <Stack className='w-fit items-center justify-center justify-start'>
          <span className='typography-body2 shrink-0 text-text-secondary mt-5'>{indexDisplay}</span>
        </Stack>
        <Stack className='w-full' spacing={2}>
          <Stack direction='row' spacing={4} centerMain className='w-full h-full'>
            <FormField
              control={form.control}
              name={`${fieldBaseName}.title` as any}
              rules={{ required: true }}
              render={({ field: { value, ...field } }) => (
                <FormItem className='grow'>
                  <FormControl>
                    <TextInput
                      value={value as string}
                      className='w-full'
                      placeholder={t('global.workstream.placeholders.milestonesTitle')}
                      {...field}
                    />
                  </FormControl>
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name={`${fieldBaseName}.status` as any}
              render={({ field: { ref, value, onChange, ...field } }) => (
                <FormItem className='h-full'>
                  <Select value={value as string} onValueChange={onChange} {...field}>
                    <FormControl>
                      <SelectTrigger ref={ref} className='w-36 shrink-0' />
                    </FormControl>
                    <SelectContent>
                      <SelectGroup>
                        {Object.values(RoadmapNodeStatus).map((status) => (
                          <SelectItem key={status} value={status}>
                            {t(`shared.goals.roadmapNodeStatus.${status}`)}
                          </SelectItem>
                        ))}
                      </SelectGroup>
                    </SelectContent>
                  </Select>
                </FormItem>
              )}
            />
            {!isNullish(childrenCount) && (
              <Pill size='small' className='shrink-0 border border-divider text-text-secondary'>
                {t('global.workstream.labels.stepsCount', { count: childrenCount })}
              </Pill>
            )}
            <IconButton onClick={onRemove}>
              <RiDeleteBinFill />
            </IconButton>
          </Stack>
          <FormField
            control={form.control}
            name={`${fieldBaseName}.description` as any}
            render={({ field: { value, ...field } }) => (
              <FormItem>
                <FormControl>
                  <textarea
                    value={value as string}
                    placeholder={t('global.workstream.placeholders.milestonesDescription')}
                    rows={1}
                    className='typography-body1 w-full resize-none border border-divider bg-neutral-black-4 px-3 py-2 text-text-primary placeholder:text-text-disabled focus:outline-none'
                    {...field}
                  />
                </FormControl>
              </FormItem>
            )}
          />
        </Stack>
      </Stack>
    </Stack>
  );
};

const StepsList = ({
  form,
  milestoneIndex,
}: {
  form: ReturnType<typeof useWorkstreamCreateContext>;
  milestoneIndex: number;
}) => {
  const { t } = useTranslation();
  const { fields, append, remove } = useFieldArray({
    control: form.control,
    name: `${WORKSTREAM_FORM_KEYS.MILESTONES}.${ROOT_ROADMAP_NODE_INDEX}.children.${milestoneIndex}.children` as any,
  });

  return (
    <Stack spacing={2} className='p-3 w-full pl-12'>
      {fields.length === 0 && (
        <span className='typography-body1 text-text-secondary'>{t('global.workstream.labels.noStepsHint')}</span>
      )}
      <Stack spacing={2} className='border-l border-divider'>
        {fields.map((field, childIndex) => (
          <MileStoneForm
            key={field.id}
            form={form}
            milestoneIndex={milestoneIndex}
            index={childIndex}
            onRemove={() => remove(childIndex)}
          />
        ))}
      </Stack>
      <Stack className='w-full items-end'>
        <Button
          variant='outline'
          size='small'
          className='w-fit rounded-full border-dashed'
          onClick={() => append(EMPTY_MILESTONE)}
          leftAdornment={<RiAddLine />}
        >
          {t('global.workstream.actions.addStep')}
        </Button>
      </Stack>
    </Stack>
  );
};
